import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  UserCircle,
  LogOut,
  History,
} from "lucide-react";
import silpiLogo from "@/assets/silpi-logo.png";
import { cn } from "@/lib/utils";
import { useLayoutStyle } from "@/hooks/use-layout-style";
import { useAuth } from "@/auth/useAuth";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { resolvedMenu as menuItems, type ResolvedMenuItem as MenuItem } from "./resolvedMenu";

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const { setStyle } = useLayoutStyle();
  const { usuario, logout } = useAuth();

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  const isMenuActive = (item: MenuItem) => {
    if (item.href) return isActive(item.href);
    if (item.subItems) return item.subItems.some((sub) => isActive(sub.href));
    return false;
  };

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  const isExpanded = (label: string) => expandedMenus.includes(label);

  const NavItem = ({ item }: { item: MenuItem }) => {
    const active = isMenuActive(item);
    const Icon = item.icon;
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const expanded = isExpanded(item.label);

    if (!hasSubItems && item.href) {
      const content = (
        <Link
          to={item.href}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group",
            active
              ? "bg-white/15 text-white font-medium"
              : "text-white/80 hover:bg-white/10 hover:text-white",
          )}
        >
          <Icon className={cn("h-5 w-5 flex-shrink-0", active ? "text-white" : "text-white/80")} />
          {!collapsed && <span className="text-sm truncate">{item.label}</span>}
        </Link>
      );

      if (collapsed) {
        return (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            <TooltipContent side="right" className="font-medium">
              {item.label}
            </TooltipContent>
          </Tooltip>
        );
      }
      return content;
    }

    const menuButton = (
      <button
        onClick={() => !collapsed && toggleMenu(item.label)}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group",
          active
            ? "bg-white/15 text-white font-medium"
            : "text-white/80 hover:bg-white/10 hover:text-white",
        )}
      >
        <Icon className={cn("h-5 w-5 flex-shrink-0", active ? "text-white" : "text-white/80")} />
        {!collapsed && (
          <>
            <span className="text-sm truncate flex-1 text-left">{item.label}</span>
            <ChevronDown
              className={cn(
                "h-4 w-4 flex-shrink-0 transition-transform duration-200",
                expanded ? "rotate-180" : "",
              )}
            />
          </>
        )}
      </button>
    );

    if (collapsed) {
      return (
        <DropdownMenu>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>{menuButton}</DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" className="font-medium">
              {item.label}
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent side="right" align="start" className="w-56 max-h-80 overflow-y-auto bg-popover">
            {item.subItems?.map((sub) => (
              <DropdownMenuItem key={sub.href} asChild>
                <Link
                  to={sub.href}
                  className={cn("w-full cursor-pointer", isActive(sub.href) && "bg-accent text-accent-foreground font-medium")}
                >
                  {sub.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <div>
        {menuButton}
        {expanded && !collapsed && (
          <div className="ml-4 mt-1 space-y-0.5 border-l border-white/20 pl-3">
            {item.subItems?.map((sub) => (
              <Link
                key={sub.href}
                to={sub.href}
                className={cn(
                  "block px-3 py-2 rounded-lg text-sm transition-all duration-200",
                  isActive(sub.href) ? "bg-white/15 text-white font-medium" : "text-white/70 hover:bg-white/10 hover:text-white",
                )}
              >
                {sub.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-primary transition-all duration-300 flex flex-col",
        collapsed ? "w-16" : "w-72",
      )}
    >
      <div className="flex items-center justify-between h-16 px-4 border-b border-white/10 flex-shrink-0">
        {!collapsed && (
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center p-1.5">
              <img src={silpiLogo} alt="Silpi" className="h-full w-auto object-contain" />
            </div>
            <span className="text-white font-semibold text-xl">Silpi Admin</span>
          </Link>
        )}
        {collapsed && (
          <Link to="/" className="mx-auto">
            <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center p-1.5">
              <img src={silpiLogo} alt="Silpi" className="h-full w-auto object-contain" />
            </div>
          </Link>
        )}
      </div>

      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 z-10 h-6 w-6 rounded-full bg-white border border-border shadow-soft flex items-center justify-center hover:bg-muted transition-colors"
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>

      <ScrollArea className="flex-1 px-3 py-4">
        <nav>
          <ul className="space-y-1">
            {menuItems.map((item) => (
              <li key={item.label}>
                <NavItem item={item} />
              </li>
            ))}
          </ul>
        </nav>
      </ScrollArea>

      <div className="px-3 py-4 border-t border-white/10 flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "w-full flex items-center gap-3 p-2 rounded-lg bg-white/10 hover:bg-white/15 transition-colors",
                collapsed ? "justify-center" : "",
              )}
            >
              <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <UserCircle className="h-5 w-5 text-white" />
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-white truncate">{usuario?.nome ?? "—"}</p>
                </div>
              )}
              {!collapsed && <ChevronDown className="h-4 w-4 text-white/60 flex-shrink-0" />}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side={collapsed ? "right" : "top"} align={collapsed ? "start" : "center"} className="w-64 bg-popover">
            <DropdownMenuItem onClick={() => setStyle("legacy")} className="cursor-pointer">
              <History className="h-4 w-4 mr-2" />
              Voltar ao menu antigo
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive cursor-pointer">
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
